import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { CheckoutEndpoint } from '../endpoints/checkout';
import { lastValueFrom, Observable, Subject } from 'rxjs';
import {
  convertFormatDate,
  validateDateEqual,
  validateDateIntersection,
  validateHourEqual,
  validateHourIntersection,
} from '@shared/utils/date';
import { STORAGE_KEYS_CONSTANTS } from '../constants/storage-keys';
import { Storage } from './storage';
import { EpaycoTransaction, IBookingDates } from '../models';
import { Authentication } from './authentication';

@Injectable({
  providedIn: 'root',
})
export class Checkout {
  private readonly _http = inject(HttpClient);
  private readonly storage = inject(Storage);
  private readonly auth = inject(Authentication);

  private payment$ = new Subject<void>();
  private activatePayment$ = new Subject<any>();
  private checkoutStorage$ = new Subject<void>();

  constructor() {}

  onPayment(): void {
    this.payment$.next();
  }

  getPayment$(): Observable<void> {
    return this.payment$.asObservable();
  }

  onActivatePayment(sale: any): void {
    this.activatePayment$.next(sale);
  }

  getActivatePayment$(): Observable<any> {
    return this.activatePayment$.asObservable();
  }

  onCheckoutStorage(): void {
    this.checkoutStorage$.next();
  }

  getCheckoutStorage$(): Observable<void> {
    return this.checkoutStorage$.asObservable();
  }

  getTransactionEpayco(refPayco: string): Observable<EpaycoTransaction> {
    return this._http.get<EpaycoTransaction>(CheckoutEndpoint.epayco + refPayco);
  }

  async addCheckout({
    data,
    typeService,
    dates,
  }: {
    data: any;
    typeService: any;
    dates: IBookingDates;
  }): Promise<any> {
    let response;
    const auth = this.auth.getAuthInformation();
    console.log({ auth });
    let checkoutData = {};

    if (auth) {
      const { id, first_name, last_name, email, phone } = auth;
      checkoutData = {
        registered_customer: id,
        customer_name: `${first_name} ${last_name}`,
        customer_last_name: `${first_name} ${last_name}`,
        customer_email: email,
        cell_phone: phone,
      };
    }

    try {
      response = await lastValueFrom(this.createCheckout(checkoutData));
    } catch (error) {
      console.error(error);
      throw new Error('error_reservation_cart');
    }

    const { id } = response;

    try {
      const checkoutLinesData = this.getObjectCheckoutLines({
        idCheckout: id,
        typeService,
        data,
        dates,
      });

      console.log({ checkoutLinesData, dates });
      const checkoutLine = await lastValueFrom(this.createCheckoutLines(checkoutLinesData));

      const { start_date, end_date } = checkoutLinesData;
      checkoutLine.start_date = start_date;
      checkoutLine.end_date = end_date;

      return {
        id,
        checkoutLine,
        typeService,
      };
    } catch (error) {
      this.deleteCheckout(id).subscribe();
      throw new Error('error_reservation_cart');
    }
  }

  async updateCheckout({
    checkout,
    typeService,
    data,
    dates,
  }: {
    checkout: any;
    typeService: any;
    data: any;
    dates: IBookingDates;
  }): Promise<any> {
    const { id, checkoutLines } = checkout;
    const { isServiceByHour, isServiceByDay, isServiceByNight } = typeService;
    console.log(checkoutLines, data);
    const checkoutLinesFilter = checkoutLines.filter((line: any) => line.product === data.id);

    const validationParams = {
      checkoutLines: checkoutLinesFilter,
      isServiceByDay,
      dates,
    };

    console.log({ checkoutLinesFilter, validationParams });

    if (checkoutLinesFilter && (isServiceByDay || isServiceByNight)) {
      if (!isServiceByHour && validateDateIntersection(validationParams)) {
        throw new Error('dates_intersect_label');
      }
      if (isServiceByHour && validateHourIntersection(validationParams)) {
        throw new Error('hours_intersect_label');
      }
    }

    let indexDate;
    if (isServiceByHour) {
      indexDate = validateHourEqual(validationParams);
    } else {
      indexDate = validateDateEqual(validationParams);
    }

    console.log({ indexDate });
    // return;

    const checkoutLinesData = this.getObjectCheckoutLines({
      idCheckout: id,
      typeService,
      data,
      dates,
    });

    console.log({ indexDate });

    try {
      let response;
      if (indexDate !== -1) {
        response = await lastValueFrom(
          this.updateCheckoutLines(checkoutLinesFilter[indexDate].id, checkoutLinesData),
        );
        response.update = true;
      } else {
        response = await lastValueFrom(this.createCheckoutLines(checkoutLinesData));
      }

      return {
        id,
        checkoutLine: response,
        typeService,
      };
    } catch (error) {
      console.error(error);
      throw new Error('Error al actualizar el checkout');
    }
  }

  async setCheckoutStorageFromUser(idUser: number): Promise<void> {
    let response;
    try {
      response = await lastValueFrom(this.getCheckout({ idUser }));
      console.log({ response });
      this.setCheckoutStorage(response?.results);
    } catch (error) {
      console.error(error);
      throw error;
    }
  }

  setCheckoutStorage(checkout: any[]): void {
    if (!checkout || checkout.length === 0) return;

    const formatDate = (date: string) => {
      const [date2, time] = date.split(' ');
      const [day, month, year] = date2.split('-');
      return `${year}-${month}-${day}T${time}`;
    };

    const checkoutStorage = {
      id: checkout[0].checkout,
      checkoutLines: checkout.map((line) => {
        return {
          id: line.id,
          product: line.product.id,
          quantity: line.quantity,
          isServiceDay: line.product.type_book_service.is_service_day,
          isServiceHour: line.product.type_book_service.is_service_hour,
          isServiceNight: line.product.type_book_service.is_service_night,
          startDate: formatDate(line.start_date),
          endDate: formatDate(line.end_date),
        };
      }),
    };

    this.storage.setLocal(STORAGE_KEYS_CONSTANTS.CHECKOUT, checkoutStorage);
    this.onCheckoutStorage();
  }

  getObjectCheckoutLines({
    data,
    typeService,
    idCheckout,
    dates,
  }: {
    data: any;
    typeService: any;
    idCheckout: number;
    dates: IBookingDates;
  }): any {
    const { id, quantity, available } = data;
    const { startDate, endDate, startTime, endTime } = dates;
    const { isServiceByHour, isServiceByDay } = typeService;
    return {
      quantity,
      start_date: convertFormatDate({ date: startDate, time: startTime }),
      end_date: convertFormatDate({
        date: isServiceByHour || isServiceByDay ? startDate : endDate,
        time: endTime,
      }),
      checkout: idCheckout,
      product: id,
      variants: available.variants_ids.slice(0, quantity),
    };
  }

  getCheckout({ idCheckout, idUser }: { idCheckout?: number; idUser?: number }): Observable<any> {
    return this._http.get(
      `${CheckoutEndpoint.checkoutLinesByCheckout}?checkout=${
        idCheckout ?? ''
      }&checkout__registered_customer=${idUser ?? ''}`,
    );
  }

  createCheckout(data: any): Observable<any> {
    return this._http.post(CheckoutEndpoint.checkout, data);
  }

  updateDataCheckout(idCheckout: number, data: any): Observable<any> {
    return this._http.patch(`${CheckoutEndpoint.checkout}${idCheckout}/`, data);
  }

  deleteCheckout(idCheckout: number): Observable<any> {
    return this._http.delete(`${CheckoutEndpoint.checkout}${idCheckout}/`);
  }

  createCheckoutLines(data: any): Observable<any> {
    return this._http.post(CheckoutEndpoint.checkoutLines, data);
  }

  updateCheckoutLines(id: number, data: any): Observable<any> {
    return this._http.patch(`${CheckoutEndpoint.checkoutLines}${id}/`, data);
  }

  deleteCheckoutLines(id: number): Observable<any> {
    return this._http.delete(`${CheckoutEndpoint.checkoutLines}${id}/`);
  }

  createCheckoutStorage(data: any): void {
    const { id, checkoutLine, typeService } = data;

    const { id: idCheckoutLine, product, start_date, end_date, quantity } = checkoutLine;

    const { isServiceByHour, isServiceByDay, isServiceByNight } = typeService;

    const checkout = {
      id,
      checkoutLines: [
        {
          id: idCheckoutLine,
          product,
          startDate: start_date,
          endDate: end_date,
          quantity,
          isServiceByHour,
          isServiceByDay,
          isServiceByNight,
        },
      ],
    };

    this.storage.setLocal(STORAGE_KEYS_CONSTANTS.CHECKOUT, checkout);
  }

  updateCheckoutLineStorage(checkout: any, data: any): void {
    const { checkoutLine, typeService } = data;
    const { id, product, start_date, end_date, quantity } = checkoutLine;

    const { isServiceByHour, isServiceByDay, isServiceByNight } = typeService;

    const index = checkout.checkoutLines.findIndex((item: any) => item.id === id);

    const obj = {
      id,
      product,
      startDate: start_date,
      endDate: end_date,
      quantity,
      isServiceByHour,
      isServiceByDay,
      isServiceByNight,
    };

    if (index === -1) {
      checkout.checkoutLines.push(obj);
    } else {
      checkout.checkoutLines[index] = obj;
    }
    this.storage.setLocal(STORAGE_KEYS_CONSTANTS.CHECKOUT, checkout);
  }

  deleteCheckoutLineStorage(checkoutStorage: any, idCheckoutLine: string): void {
    if (!checkoutStorage) return;

    checkoutStorage.checkoutLines = checkoutStorage.checkoutLines.filter(
      (item: any) => item.id !== idCheckoutLine,
    );
    this.storage.setLocal(STORAGE_KEYS_CONSTANTS.CHECKOUT, checkoutStorage);
  }

  createSale(idCheckout: number): Observable<any> {
    return this._http.post(CheckoutEndpoint.sale, { checkout_id: idCheckout });
  }
}
